/**
 * ERC Designator Conflicts Validator Adapter for DRC System
 *
 * This validator checks component reference designators (e.g., U1, R1, C1) for:
 * - Identical designators (error): Multiple components with the same reference designator
 * - Similar designators (warning): Designators that differ only in case or whitespace
 *
 * Note: This checks component designators only, not net labels.
 *
 * Feature-gated via DrcEnabledCheckers.ErcSimilarLabelsValidator
 */
import { EditorModes, ReviewType } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
/**
 * Validator that checks for similar or duplicate component reference designators.
 *
 * This validator compares all component reference designators (e.g., U1, R1, C1) and reports:
 * - Identical designators as errors (duplicate naming)
 * - Similar designators (case/whitespace differences only) as warnings
 */
export class ErcSimilarLabelsValidator {
    reviewType = ReviewType.DesignRuleCheckErcSimilarLabels;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = true;
    async checkForProblems(inputs) {
        const foundProblems = [];
        // Collect all elements with designators (labels)
        const elementsWithDesignators = [];
        for (const [uid, element] of Object.entries(inputs.elements)) {
            const designator = element.label;
            if (designator && !this.isAutoGeneratedDesignator(designator)) {
                elementsWithDesignators.push({ uid, designator });
            }
        }
        // Group elements by normalized designator (case-insensitive, whitespace-collapsed)
        const normalizeForGrouping = (s) => s.trim().replace(/\s+/g, " ").toLowerCase();
        const designatorGroups = new Map();
        for (const elem of elementsWithDesignators) {
            const normalizedKey = normalizeForGrouping(elem.designator);
            const group = designatorGroups.get(normalizedKey) ?? [];
            group.push(elem);
            designatorGroups.set(normalizedKey, group);
        }
        // Report problems for groups with multiple elements
        for (const [_normalizedKey, group] of designatorGroups) {
            if (group.length < 2)
                continue;
            // Check if all designators in the group are truly identical or just similar
            const firstDesignator = group[0].designator.trim().replace(/\s+/g, " ");
            const allIdentical = group.every((elem) => elem.designator.trim().replace(/\s+/g, " ") === firstDesignator);
            // Collect all unique designator variants for the message
            const uniqueDesignators = [...new Set(group.map((e) => e.designator))];
            const message = allIdentical
                ? `Duplicate designators: "${firstDesignator}" appears on ${group.length} components.`
                : `Similar designators: ${uniqueDesignators
                    .map((d) => `"${d}"`)
                    .join(", ")} differ only in case or whitespace.`;
            // Sort UIDs for consistent problem key
            const sortedUids = group.map((e) => e.uid).sort();
            foundProblems.push({
                problemTypeKey: this.problemTypeKey,
                key: `${this.problemTypeKey}_${sortedUids.join("_")}`,
                label: message,
                affectedItems: group.map((elem) => ({ type: "element", uid: elem.uid })),
                affectedViews: [EditorModes.schematic],
            });
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
    /**
     * Checks if a designator appears to be auto-generated.
     */
    isAutoGeneratedDesignator(designator) {
        const autoPatterns = [/^Net[-_]?\d+$/i, /^N\$\d+$/i, /^unconnected[-_]/i, /^\d+$/];
        return autoPatterns.some((pattern) => pattern.test(designator));
    }
}
//# sourceMappingURL=ErcSimilarLabelsValidator.js.map